import { useState, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import Icon from "./Icon";
import { format, isToday, isYesterday, isAfter, isBefore, subDays, startOfDay, endOfDay, parseISO } from "date-fns";
import { useI18n } from "../../i18n/LanguageContext";

export default function AdvancedTable({
  columns,
  data,
  actions,
  searchPlaceholder = "Ara...",
  onRowClick,
}) {
  const { t, lang } = useI18n();
  const [globalSearch, setGlobalSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Column-specific filter states: { [columnKey]: { operator, value, value2 } }
  const [columnFilters, setColumnFilters] = useState({});
  const [showFilterPopover, setShowFilterPopover] = useState(null); // column key

  // Get active operator display names
  const stringOperators = [
    { value: "contains", label: "İçerir" },
    { value: "startsWith", label: "İle Başlar" },
    { value: "endsWith", label: "İle Biter" },
    { value: "equals", label: "Eşittir" },
    { value: "notEquals", label: "Eşit Değildir" },
    { value: "isEmpty", label: "Boştur" },
  ];

  const dateOperators = [
    { value: "before", label: "Önce" },
    { value: "after", label: "Sonra" },
    { value: "between", label: "Arasında" },
    { value: "equals", label: "Eşittir" },
    { value: "notEquals", label: "Eşit Değildir" },
    { value: "isEmpty", label: "Boştur" },
    { value: "isNotEmpty", label: "Dolu" },
    { value: "today", label: "Bugün" },
    { value: "yesterday", label: "Dün" },
    { value: "last7", label: "Son 7 Gün" },
    { value: "last30", label: "Son 30 Gün" },
  ];

  // Reset page on filter/search change
  const handleFilterChange = (key, filterObj) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: filterObj,
    }));
    setCurrentPage(1);
  };

  const handleGlobalSearchChange = (val) => {
    setGlobalSearch(val);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setColumnFilters({});
    setGlobalSearch("");
    setCurrentPage(1);
  };

  // 1. Filter Logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      // Global Search
      if (globalSearch) {
        const matchesGlobal = columns.some((col) => {
          const val = row[col.key];
          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(globalSearch.toLowerCase());
        });
        if (!matchesGlobal) return false;
      }

      // Column Filters
      for (const colKey of Object.keys(columnFilters)) {
        const filter = columnFilters[colKey];
        if (!filter || !filter.operator) continue;

        const col = columns.find((c) => c.key === colKey);
        const cellValue = row[colKey];
        const { operator, value, value2 } = filter;

        if (col?.type === "date") {
          const dateVal = cellValue ? (cellValue instanceof Date ? cellValue : parseISO(cellValue)) : null;
          
          switch (operator) {
            case "isEmpty":
              if (dateVal !== null) return false;
              break;
            case "isNotEmpty":
              if (dateVal === null) return false;
              break;
            case "today":
              if (!dateVal || !isToday(dateVal)) return false;
              break;
            case "yesterday":
              if (!dateVal || !isYesterday(dateVal)) return false;
              break;
            case "last7": {
              if (!dateVal) return false;
              const sevenDaysAgo = subDays(new Date(), 7);
              if (isBefore(dateVal, startOfDay(sevenDaysAgo)) || isAfter(dateVal, endOfDay(new Date()))) return false;
              break;
            }
            case "last30": {
              if (!dateVal) return false;
              const thirtyDaysAgo = subDays(new Date(), 30);
              if (isBefore(dateVal, startOfDay(thirtyDaysAgo)) || isAfter(dateVal, endOfDay(new Date()))) return false;
              break;
            }
            case "before":
              if (!dateVal || !value || isAfter(dateVal, startOfDay(parseISO(value)))) return false;
              break;
            case "after":
              if (!dateVal || !value || isBefore(dateVal, endOfDay(parseISO(value)))) return false;
              break;
            case "equals": {
              if (!dateVal || !value) return false;
              const compareDate = parseISO(value);
              if (format(dateVal, "yyyy-MM-dd") !== format(compareDate, "yyyy-MM-dd")) return false;
              break;
            }
            case "notEquals": {
              if (!dateVal || !value) return false;
              const compareDate = parseISO(value);
              if (format(dateVal, "yyyy-MM-dd") === format(compareDate, "yyyy-MM-dd")) return false;
              break;
            }
            case "between":
              if (!dateVal || !value || !value2) return false;
              const start = startOfDay(parseISO(value));
              const end = endOfDay(parseISO(value2));
              if (isBefore(dateVal, start) || isAfter(dateVal, end)) return false;
              break;
            default:
              break;
          }
        } else {
          // String filter
          const strVal = cellValue !== null && cellValue !== undefined ? String(cellValue).toLowerCase() : "";
          const targetVal = value ? String(value).toLowerCase() : "";

          switch (operator) {
            case "contains":
              if (!strVal.includes(targetVal)) return false;
              break;
            case "startsWith":
              if (!strVal.startsWith(targetVal)) return false;
              break;
            case "endsWith":
              if (!strVal.endsWith(targetVal)) return false;
              break;
            case "equals":
              if (strVal !== targetVal) return false;
              break;
            case "notEquals":
              if (strVal === targetVal) return false;
              break;
            case "isEmpty":
              if (strVal !== "") return false;
              break;
            default:
              break;
          }
        }
      }

      return true;
    });
  }, [data, globalSearch, columnFilters, columns]);

  // 2. Sort Logic
  const sortedData = useMemo(() => {
    const sortable = [...filteredData];
    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (valA === undefined || valA === null) valA = "";
        if (valB === undefined || valB === null) valB = "";

        if (typeof valA === "string" && typeof valB === "string") {
          return sortConfig.direction === "asc"
            ? valA.localeCompare(valB, "tr")
            : valB.localeCompare(valA, "tr");
        }

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  // 3. Pagination Logic
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize) || 1;

  const handleSort = (key, sortable) => {
    if (!sortable) return;
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Export functions
  const exportToCSV = () => {
    const headers = columns.map((col) => col.header).join(",");
    const rows = sortedData.map((row) =>
      columns
        .map((col) => {
          const val = row[col.key];
          return `"${String(val ?? "").replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `export_${format(new Date(), "yyyyMMdd_HHmmss")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export (PDF/Excel) için hücrenin DÜZ METİN karşılığı. Öncelik:
  // 1) col.exportValue(row) — sayfa özel düz metin verirse onu kullan.
  // 2) col.render(...) — ekrandaki görünümü static markup'a çevirip ikon/buton/svg
  //    çıkararak metni al (böylece INCOME→"Gelir", ISSUED→"Kesildi", tarih/tutar
  //    biçimli hâliyle export edilir; ham enum değil).
  // 3) row[col.key] — ham değer (son çare).
  const cellText = (col, row) => {
    if (typeof col.exportValue === "function") {
      const v = col.exportValue(row);
      return v == null ? "" : String(v);
    }
    if (typeof col.render === "function") {
      try {
        const markup = renderToStaticMarkup(col.render(row[col.key], row));
        const tmp = document.createElement("div");
        tmp.innerHTML = markup;
        tmp.querySelectorAll(".material-symbols-outlined, button, svg").forEach((el) => el.remove());
        const text = tmp.textContent.replace(/\s+/g, " ").trim();
        if (text) return text;
      } catch {
        /* render başarısızsa ham değere düş */
      }
    }
    const raw = row[col.key];
    return raw == null ? "" : String(raw);
  };

  const exportToExcel = () => {
    // Basic XML Format for Excel download (retains UTF-8 character coding)
    let xml = `
      <xml xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
        <Workbook>
          <Worksheet name="Data">
            <Table>
              <Row>
                ${columns.map((col) => `<Cell><Data ss:Type="String">${col.header}</Data></Cell>`).join("")}
              </Row>
    `;

    sortedData.forEach((row) => {
      xml += "<Row>";
      columns.forEach((col) => {
        const val = cellText(col, row);
        xml += `<Cell><Data ss:Type="String">${String(val).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</Data></Cell>`;
      });
      xml += "</Row>";
    });

    xml += `
            </Table>
          </Worksheet>
        </Workbook>
      </xml>
    `;

    const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `export_${format(new Date(), "yyyyMMdd_HHmmss")}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const esc = (s) =>
      String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const pdfLocale = lang === "en" ? "en-GB" : "tr-TR";
    const printWindow = window.open("", "_blank");
    const html = `
      <html>
        <head>
          <title>${esc(t("table.pdf.title"))}</title>
          <style>
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
            th { background-color: #f5f5f5; font-weight: bold; }
            h2 { color: #7c3aed; margin-bottom: 5px; }
            p { font-size: 11px; color: #666; margin-top: 0; }
          </style>
        </head>
        <body>
          <h2>${esc(t("table.pdf.title"))}</h2>
          <p>${esc(t("table.pdf.date"))}: ${esc(new Date().toLocaleString(pdfLocale, { dateStyle: "short", timeStyle: "short" }))}</p>
          <table>
            <thead>
              <tr>
                ${columns.map((col) => `<th>${esc(col.header)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${sortedData
                .map(
                  (row) => `
                <tr>
                  ${columns.map((col) => `<td>${esc(cellText(col, row)) || "—"}</td>`).join("")}
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-4">
      {/* Top Action Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface-card p-4 rounded-2xl border border-outline-variant shadow-sm">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={globalSearch}
            onChange={(e) => handleGlobalSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-outline-variant rounded-xl focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm bg-white"
          />
          <Icon name="search" className="absolute left-3 top-2.5 text-on-surface-variant !text-[18px]" />
          {globalSearch && (
            <button
              onClick={() => handleGlobalSearchChange("")}
              className="absolute right-3 top-2.5 text-on-surface-variant hover:text-primary transition"
            >
              <Icon name="close" className="!text-[18px]" />
            </button>
          )}
        </div>

        {/* Actions & Export */}
        <div className="flex items-center flex-wrap gap-2.5">
          {Object.keys(columnFilters).length > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-error bg-error/10 hover:bg-error/15 rounded-xl transition"
            >
              <Icon name="filter_alt_off" className="!text-[16px]" /> Filtreleri Temizle
            </button>
          )}

          {/* Export Dropdown */}
          <div className="flex items-center border border-outline-variant rounded-xl overflow-hidden bg-white">
            <button
              onClick={exportToExcel}
              title="Excel'e Aktar"
              className="p-2 text-on-surface-variant hover:text-success hover:bg-success/5 transition border-r border-outline-variant"
            >
              <Icon name="description" className="!text-[20px]" />
            </button>
            <button
              onClick={exportToCSV}
              title="CSV'ye Aktar"
              className="p-2 text-on-surface-variant hover:text-primary hover:bg-primary/5 transition border-r border-outline-variant"
            >
              <Icon name="csv" className="!text-[20px]" />
            </button>
            <button
              onClick={exportToPDF}
              title="Yazdır / PDF"
              className="p-2 text-on-surface-variant hover:text-error hover:bg-error/5 transition"
            >
              <Icon name="picture_as_pdf" className="!text-[20px]" />
            </button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant overflow-visible">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-container-low text-on-surface-variant text-left border-b border-outline-variant select-none">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-5 py-4 font-semibold uppercase text-xs tracking-wide relative group"
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        onClick={() => handleSort(col.key, col.sortable !== false)}
                        className={`cursor-pointer hover:text-primary transition flex items-center gap-1 ${
                          sortConfig.key === col.key ? "text-primary font-bold" : ""
                        }`}
                      >
                        {col.header}
                        {col.sortable !== false && sortConfig.key === col.key && (
                          <Icon
                            name={sortConfig.direction === "asc" ? "arrow_upward" : "arrow_downward"}
                            className="!text-[14px]"
                          />
                        )}
                        {col.sortable !== false && sortConfig.key !== col.key && (
                          <Icon name="swap_vert" className="!text-[14px] opacity-0 group-hover:opacity-60 transition" />
                        )}
                      </span>

                      {/* Column Filter Toggle Button */}
                      {col.filterable !== false && (
                        <div className="relative inline-block">
                          <button
                            type="button"
                            onClick={() => setShowFilterPopover(showFilterPopover === col.key ? null : col.key)}
                            className={`p-1 rounded-lg transition hover:bg-surface-container-high ${
                              columnFilters[col.key]?.operator ? "text-primary bg-primary/10" : "text-on-surface-variant opacity-40 group-hover:opacity-100"
                            }`}
                          >
                            <Icon name="filter_alt" className="!text-[14px]" />
                          </button>

                          {/* Column Filter Popover */}
                          {showFilterPopover === col.key && (
                            <div className="absolute left-0 mt-2 w-64 bg-white border border-outline-variant rounded-2xl shadow-xl p-4 z-50 normal-case text-on-surface">
                              <div className="flex items-center justify-between border-b border-outline-variant pb-2 mb-3">
                                <span className="font-semibold text-xs text-on-surface-variant">
                                  {col.header} Filtresi
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setShowFilterPopover(null)}
                                  className="text-on-surface-variant hover:text-primary"
                                >
                                  <Icon name="close" className="!text-[16px]" />
                                </button>
                              </div>

                              <div className="space-y-3 text-xs">
                                <div>
                                  <label className="block text-[11px] font-medium text-on-surface-variant mb-1">
                                    Koşul
                                  </label>
                                  <select
                                    value={columnFilters[col.key]?.operator ?? ""}
                                    onChange={(e) => {
                                      const op = e.target.value;
                                      if (!op) {
                                        handleFilterChange(col.key, null);
                                      } else {
                                        handleFilterChange(col.key, {
                                          operator: op,
                                          value: columnFilters[col.key]?.value ?? "",
                                          value2: columnFilters[col.key]?.value2 ?? "",
                                        });
                                      }
                                    }}
                                    className="w-full border border-outline-variant rounded-lg p-2 bg-white outline-none focus:border-primary text-xs"
                                  >
                                    <option value="">Filtre Yok</option>
                                    {(col.type === "date" ? dateOperators : stringOperators).map((op) => (
                                      <option key={op.value} value={op.value}>
                                        {op.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                {/* Operator Inputs based on Operator Type */}
                                {columnFilters[col.key]?.operator &&
                                  !["isEmpty", "isNotEmpty", "today", "yesterday", "last7", "last30"].includes(
                                    columnFilters[col.key].operator
                                  ) && (
                                    <div className="space-y-2">
                                      <div>
                                        <label className="block text-[11px] font-medium text-on-surface-variant mb-1">
                                          {columnFilters[col.key].operator === "between" ? "Başlangıç" : "Değer"}
                                        </label>
                                        <input
                                          type={col.type === "date" ? "date" : "text"}
                                          value={columnFilters[col.key]?.value ?? ""}
                                          onChange={(e) =>
                                            handleFilterChange(col.key, {
                                              ...columnFilters[col.key],
                                              value: e.target.value,
                                            })
                                          }
                                          className="w-full border border-outline-variant rounded-lg p-2 bg-white outline-none focus:border-primary text-xs"
                                        />
                                      </div>

                                      {columnFilters[col.key].operator === "between" && (
                                        <div>
                                          <label className="block text-[11px] font-medium text-on-surface-variant mb-1">
                                            Bitiş
                                          </label>
                                          <input
                                            type="date"
                                            value={columnFilters[col.key]?.value2 ?? ""}
                                            onChange={(e) =>
                                              handleFilterChange(col.key, {
                                                ...columnFilters[col.key],
                                                value2: e.target.value,
                                              })
                                            }
                                            className="w-full border border-outline-variant rounded-lg p-2 bg-white outline-none focus:border-primary text-xs"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}

                                <div className="flex gap-2 justify-end pt-2 border-t border-outline-variant mt-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleFilterChange(col.key, null);
                                      setShowFilterPopover(null);
                                    }}
                                    className="px-2.5 py-1.5 text-on-surface-variant hover:text-primary font-medium"
                                  >
                                    Temizle
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setShowFilterPopover(null)}
                                    className="px-3 py-1.5 bg-primary text-white font-semibold rounded-lg hover:opacity-90"
                                  >
                                    Uygula
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                ))}
                {actions && (
                  <th className="px-5 py-4 font-semibold uppercase text-xs tracking-wide text-right">
                    İşlemler
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (actions ? 1 : 0)}
                    className="p-8 text-center text-on-surface-variant font-medium"
                  >
                    Eşleşen kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, rowIndex) => (
                  <tr
                    key={row.id ?? rowIndex}
                    onClick={() => onRowClick && onRowClick(row)}
                    className={`transition-colors ${
                      onRowClick ? "cursor-pointer hover:bg-primary/5" : "hover:bg-primary/5"
                    }`}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-5 py-3.5 text-on-surface">
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                      </td>
                    ))}
                    {actions && (
                      <td
                        className="px-5 py-3.5 text-right space-x-2.5 whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()} // Prevents row click trigger when clicking actions
                      >
                        {actions(row)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        {sortedData.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-outline-variant select-none">
            <div className="text-xs text-on-surface-variant">
              Toplam <strong>{sortedData.length}</strong> kayıt arasından{" "}
              <strong>{Math.min((currentPage - 1) * pageSize + 1, sortedData.length)}</strong> -{" "}
              <strong>{Math.min(currentPage * pageSize, sortedData.length)}</strong> arası gösteriliyor.
            </div>

            <div className="flex items-center gap-3">
              {/* Page Size Select */}
              <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                <span>Satır:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-outline-variant rounded-lg p-1.5 bg-white outline-none focus:border-primary text-xs"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </select>
              </div>

              {/* Prev / Next Page buttons */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-outline-variant hover:bg-surface-container-high disabled:opacity-40 disabled:hover:bg-transparent transition text-on-surface-variant"
                >
                  <Icon name="chevron_left" className="!text-[18px]" />
                </button>
                <span className="text-xs text-on-surface-variant min-w-[50px] text-center">
                  <strong>{currentPage}</strong> / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-outline-variant hover:bg-surface-container-high disabled:opacity-40 disabled:hover:bg-transparent transition text-on-surface-variant"
                >
                  <Icon name="chevron_right" className="!text-[18px]" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
